import { regionalAccessClaimsSchema, type RegionalAccessClaims } from "@typedoc/contracts";
import { decodeJwt } from "jose";
import { AppError } from "./app-error";
import { boundedRequest } from "./bounded-request";

export type AccessScope = { userId: string; workspaceId: string };
export type CachedAccessToken = {
  token: string;
  claims: RegionalAccessClaims;
  signal: AbortSignal;
};

/** Only a transport cache. Cryptographic verification and authorization happen at the API. */
export function createRegionalTokenCache(
  loadToken: (signal: AbortSignal) => Promise<string>,
  now: () => number = Date.now,
  timeoutMs = 5000,
) {
  let generation = 0;
  /** How many times a held scope was dropped; see `discarded`. */
  let discards = 0;
  let scopeKey: string | undefined;
  let cached: CachedAccessToken | undefined;
  let pending: Promise<CachedAccessToken> | undefined;
  let scopeController = new AbortController();
  const tokenGenerations = new WeakMap<CachedAccessToken, number>();
  const key = (scope: AccessScope) => JSON.stringify([scope.userId, scope.workspaceId]);

  function clear() {
    // A scope is held from the first ask until a clear; nothing held, nothing dropped.
    if (scopeKey !== undefined) discards++;
    generation++;
    scopeController.abort();
    scopeController = new AbortController();
    scopeKey = undefined;
    cached = undefined;
    pending = undefined;
  }

  function get(scope: AccessScope): Promise<CachedAccessToken> {
    if (!scope.userId.trim() || !scope.workspaceId.trim()) {
      return Promise.reject(new AppError("WORKSPACE_REQUIRED"));
    }
    const requestedKey = key(scope);
    if (scopeKey !== requestedKey) {
      clear();
      scopeKey = requestedKey;
    }
    if (cached && cached.claims.exp * 1000 > now() + 30000) return Promise.resolve(cached);
    if (pending) return pending;

    const requestedGeneration = generation;
    const promise: Promise<CachedAccessToken> = (async () => {
      const token = await boundedRequest(loadToken, scopeController.signal, timeoutMs);
      let claims: RegionalAccessClaims;
      try {
        claims = regionalAccessClaimsSchema.parse(decodeJwt(token));
      } catch {
        throw new AppError("INVALID_RESPONSE");
      }
      if (
        claims.sub !== scope.userId ||
        claims.workspace_id !== scope.workspaceId ||
        claims.exp * 1000 <= now() + 30000 ||
        claims.iat * 1000 > now() + 5000 ||
        claims.exp <= claims.iat ||
        claims.exp - claims.iat > 300
      )
        throw new AppError("ACCESS_CHANGED");
      if (generation !== requestedGeneration || scopeKey !== requestedKey) {
        throw new AppError("REQUEST_CANCELLED");
      }
      cached = { token, claims, signal: scopeController.signal };
      tokenGenerations.set(cached, requestedGeneration);
      return cached;
    })().finally(() => {
      if (pending === promise) pending = undefined;
    });
    pending = promise;
    return promise;
  }

  /**
   * The API refused `stale`; the next `get` mints a new token for the same scope. Unlike `clear`,
   * this neither aborts nor invalidates sibling requests in the scope, and only the first caller
   * holding `stale` drops it, so concurrent 401s share one reissue.
   */
  function refresh(scope: AccessScope, stale: CachedAccessToken) {
    if (scopeKey === key(scope) && cached === stale) cached = undefined;
  }

  /**
   * Whether a token for `scope` can be asked for without displacing another scope's: nothing is
   * held (the cache was cleared and nobody has asked since), or what is held is this scope's. A
   * `get` for a scope that is not the held one clears the held one, so something that outlives a
   * clear checks this before it asks again.
   */
  function available(scope: AccessScope): boolean {
    return scopeKey === undefined || scopeKey === key(scope);
  }

  /**
   * How many times a scope that was held has been dropped, by a clear or by another scope being
   * asked for. Something that outlives a clear compares it before and after to tell a cache that
   * was cleared from a mint that failed for another reason (a token that names someone else).
   */
  const discarded = () => discards;

  return {
    get,
    clear,
    refresh,
    available,
    discarded,
    isCurrent: (scope: AccessScope, token: CachedAccessToken) =>
      scopeKey === key(scope) &&
      tokenGenerations.get(token) === generation &&
      !token.signal.aborted,
  };
}

export type RegionalTokenCache = ReturnType<typeof createRegionalTokenCache>;
